import { useEffect, useState, type FormEvent } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom'
import { FiestaBrandLogo } from '../components/FiestaBrandLogo'
import { Hicon } from '../components/Hicon'
import { useBiAuth } from '../state/BiAuthContext'
import { useBiTheme } from '../state/BiThemeContext'
import { THEME_OPTIONS, type BiThemeId } from '../theme'

/** Após login (ou já autenticado em /login) → sempre Home. Refresh em páginas
 * autenticadas mantém a rota — isso é o router + cookie, sem `?next=`. */
const POST_LOGIN_PATH = '/'

export function LoginPage() {
  const { login, isAuthenticated, bootstrapping, loading, error, clearError } =
    useBiAuth()
  const { themeId, setThemeId } = useBiTheme()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [mostrarSenha, setMostrarSenha] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string
    senha?: string
  }>({})

  useEffect(() => {
    clearError()
  }, [clearError])

  useEffect(() => {
    if (!bootstrapping && isAuthenticated) {
      navigate(POST_LOGIN_PATH, { replace: true })
    }
  }, [bootstrapping, isAuthenticated, navigate])

  if (bootstrapping) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (isAuthenticated) {
    return <Navigate to={POST_LOGIN_PATH} replace />
  }

  function validate() {
    const next: { email?: string; senha?: string } = {}
    if (!email.trim()) next.email = 'Email é obrigatório'
    else if (!/\S+@\S+\.\S+/.test(email)) next.email = 'Email inválido'
    if (!senha) next.senha = 'Senha é obrigatória'
    else if (senha.length < 6) next.senha = 'Senha deve ter pelo menos 6 caracteres'
    setFieldErrors(next)
    return Object.keys(next).length === 0
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!validate()) return
    try {
      await login(email, senha)
      navigate(POST_LOGIN_PATH, { replace: true })
    } catch {
      /* erro no contexto */
    }
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
          bgcolor: 'background.default',
        }}
      >
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <TextField
            select
            size="small"
            label="Tema"
            value={themeId}
            onChange={(e) => setThemeId(e.target.value as BiThemeId)}
            sx={{ minWidth: 140 }}
          >
            {THEME_OPTIONS.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 400,
          }}
        >
          <FiestaBrandLogo height={56} sx={{ mb: 2, maxWidth: 280 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Faça login para aceder ao Command Center
          </Typography>

          {error ? (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Box component="form" onSubmit={onSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (fieldErrors.email) setFieldErrors((f) => ({ ...f, email: undefined }))
              }}
              error={Boolean(fieldErrors.email)}
              helperText={fieldErrors.email}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="senha"
              label="Senha"
              type={mostrarSenha ? 'text' : 'password'}
              id="senha"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                      onClick={() => setMostrarSenha((v) => !v)}
                      onMouseDown={(e) => e.preventDefault()}
                      disabled={loading}
                    >
                      <Hicon name={mostrarSenha ? 'eye-off' : 'eye'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              autoComplete="current-password"
              value={senha}
              onChange={(e) => {
                setSenha(e.target.value)
                if (fieldErrors.senha) setFieldErrors((f) => ({ ...f, senha: undefined }))
              }}
              error={Boolean(fieldErrors.senha)}
              helperText={fieldErrors.senha}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Entrar'}
            </Button>
          </Box>

          <Link
            component={RouterLink}
            to="/esqueci-senha"
            underline="hover"
            variant="body2"
            sx={{ mt: 0.5 }}
          >
            Esqueci minha senha
          </Link>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
            Mesmos utilizadores do Fiesta Financeiro
          </Typography>
        </Paper>
      </Box>
    </Container>
  )
}
